import { useEffect, useState } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import {
  ShoppingCart,
  Star,
  ArrowLeft,
  Share2,
  Check,
  Minus,
  Plus,
  X,
} from "lucide-react";
import { supabase } from "../lib/supabase";
import { sanitizeHtml } from "../lib/sanitizeHtml";
import { useCartStore } from "../store/cartStore";
import { Product, PRODUCT_SELECT } from "../types/product";
import { Review } from "../types/supabase";
import { useAuthStore } from "../store/authStore";
import { useDocumentMeta } from "../hooks/useDocumentMeta";
import ProductGallery from "../components/ProductGallery";
import ProductCard from "../components/ProductCard";
import { formatEuro } from "../lib/utils";
import { productImage } from "../lib/productImage";
import { activeVariants, defaultVariant, hasDiscount } from "../lib/variants";
import VariantPicker from "../components/VariantPicker";

export default function ProductDetail() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [product, setProduct] = useState<Product | null>(null);
  const [reviews, setReviews] = useState<Review[]>([]);
  const [loading, setLoading] = useState(true);
  const [quantity, setQuantity] = useState(1);
  const [reviewText, setReviewText] = useState("");
  const [rating, setRating] = useState(5);
  const [relatedProducts, setRelatedProducts] = useState<Product[]>([]);
  const [addedToCart, setAddedToCart] = useState(false);
  const [activeTab, setActiveTab] = useState("reviews");
  const [variantId, setVariantId] = useState<string | null>(null);

  const addItem = useCartStore((state) => state.addItem);
  const cartItems = useCartStore((state) => state.items);
  const updateQuantity = useCartStore((state) => state.updateQuantity);
  const { user } = useAuthStore();

  useDocumentMeta(product?.name, product?.description || undefined);

  useEffect(() => {
    async function fetchProductAndReviews() {
      setLoading(true);
      if (!id) return;

      try {
        // Fetch product details including long_description
        const { data: productData, error: productError } = await supabase
          .from("products")
          .select(PRODUCT_SELECT)
          .eq("id", id)
          .eq("active", true)
          .single();

        if (productError) {
          console.error("Error fetching product:", productError);
          navigate("/products", { replace: true });
          return;
        }

        if (productData) {
          setProduct(productData);
          setVariantId(defaultVariant(productData)?.id ?? null);

          // Fetch reviews for this product
          const { data: reviewsData, error: reviewsError } = await supabase
            .from("reviews")
            .select(
              `
              id,
              product_id,
              user_id,
              rating,
              comment,
              created_at
            `
            )
            .eq("product_id", id)
            .order("created_at", { ascending: false });


          if (reviewsError) {
            console.error("Error fetching reviews:", reviewsError);
          } else {
            setReviews(reviewsData || []);
          }

          // Fetch related products in the same category
          if (productData.category) {
            const { data: relatedData, error: relatedError } = await supabase
              .from("products")
              .select(PRODUCT_SELECT)
              .eq("active", true)
              .eq("category", productData.category)
              .neq("id", id)
              .limit(4);

            if (relatedError) {
              console.error("Error fetching related products:", relatedError);
            } else {
              setRelatedProducts(relatedData || []);
            }
          }
        }
      } catch (error) {
        console.error("Error in fetchProductAndReviews:", error);
      } finally {
        setLoading(false);
      }
    }

    fetchProductAndReviews();
  }, [id, user, navigate]);

  const variants = product ? activeVariants(product) : [];
  const variant = variants.find((v) => v.id === variantId) ?? null;
  const stock = variant?.stock ?? 0;

  // Quantità già nel carrello per il formato scelto
  useEffect(() => {
    const cartItem = cartItems.find((item) => item.id === variantId);
    if (cartItem) {
      setQuantity(cartItem.quantity);
      setAddedToCart(true);
    } else {
      setQuantity(1);
      setAddedToCart(false);
    }
  }, [cartItems, variantId]);

  const handleAddToCart = () => {
    if (!product || !variant) return;

    if (addedToCart) {
      updateQuantity(variant.id, quantity);
    } else {
      addItem(product, variant, quantity);
    }
    setAddedToCart(true);
  };

  const handleQuantityChange = (newQuantity: number) => {
    if (newQuantity <= stock && newQuantity >= 1) {
      setQuantity(newQuantity);
    }
  };

  const submitReview = async () => {
    if (!user || !product || !reviewText) return;

    try {
      const { error } = await supabase.from("reviews").insert({
        product_id: product.id,
        user_id: user.id,
        rating,
        comment: reviewText,
      });

      if (error) {
        console.error("Error submitting review:", error);
        alert("Errore durante l'invio della recensione");
      } else {
        const { data: reviewsData, error: reviewsError } = await supabase
          .from("reviews")
          .select(
            `
              id,
              product_id,
              user_id,
              rating,
              comment,
              created_at
            `
          )
          .eq("product_id", id)
          .order("created_at", { ascending: false });

        if (!reviewsError) {
          setReviews(reviewsData || []);
          setReviewText("");
          setRating(3);
        }
      }
    } catch (error) {
      console.error("Error:", error);
    }
  };

  const shareProduct = async () => {
    if (navigator.share && product) {
      try {
        await navigator.share({
          title: product.name,
          text: product.description || "",
          url: window.location.href,
        });
      } catch (error) {
        console.log("Error sharing:", error);
      }
    } else {
      // Fallback: copy to clipboard
      navigator.clipboard.writeText(window.location.href);
      alert("Link copiato negli appunti!");
    }
  };

  // Calculate average rating
  const averageRating =
    reviews.length > 0
      ? reviews.reduce((acc, review) => acc + review.rating, 0) / reviews.length
      : 0;

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center py-20">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-primary"></div>
      </div>
    );
  }

  if (!product) {
    return (
      <div className="container py-10">
        <p className="text-muted-foreground">
          Prodotto non trovato.
        </p>
        <Link
          to="/products"
          className="text-primary hover:underline mt-4 inline-block"
        >
          <ArrowLeft className="inline mr-2 h-4 w-4" />
          Torna ai prodotti
        </Link>
      </div>
    );
  }

  // Un'offerta per formato: Google vede prezzo e disponibilità di ciascuno.
  const productJsonLd = {
    "@context": "https://schema.org",
    "@type": "Product",
    name: product.name,
    description: product.description || undefined,
    image: product.image_url || undefined,
    offers: variants.map((v) => ({
      "@type": "Offer",
      name: v.label,
      priceCurrency: "EUR",
      price: v.price,
      availability:
        v.stock > 0
          ? "https://schema.org/InStock"
          : "https://schema.org/OutOfStock",
      url: `https://apicolturadepiccoli.it/products/${product.id}`,
    })),
  };

  return (
    <div className="min-h-screen bg-muted">
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: JSON.stringify(productJsonLd) }}
      />
      <div className="container py-8">
        {/* Breadcrumb e navigazione */}
        <div className="mb-8">
          <nav className="flex items-center space-x-2 text-sm text-muted-foreground">
            <Link to="/" className="hover:text-primary transition-colors">
              Home
            </Link>
            <span>›</span>
            <Link
              to="/products"
              className="hover:text-primary transition-colors"
            >
              Prodotti
            </Link>
            <span>›</span>
            <span className="text-foreground truncate">
              {product.name}
            </span>
          </nav>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 lg:gap-12 mb-16">
          {/* Gallery prodotto */}
          <div className="lg:sticky lg:top-8 lg:h-fit">
            <ProductGallery
              productId={product.id}
              mainImage={productImage(product.image_url)}
            />
          </div>

          {/* Dettagli prodotto */}
          <div className="space-y-6">
            <div>
              <h1 className="text-3xl lg:text-4xl font-bold text-foreground mb-4">
                {product.name}
              </h1>

              <div className="flex items-center justify-between mb-4">
                <div className="flex items-center space-x-4">
                  {/* Valutazione media */}
                  <div className="flex items-center">
                    {[1, 2, 3, 4, 5].map((star) => (
                      <Star
                        key={star}
                        className={`h-5 w-5 ${
                          star <= Math.round(averageRating)
                            ? "text-yellow-400 fill-current"
                            : "text-muted-foreground/40"
                        }`}
                      />
                    ))}
                    <span className="ml-2 text-muted-foreground">
                      ({reviews.length}{" "}
                      {reviews.length === 1 ? "recensione" : "recensioni"})
                    </span>
                  </div>

                  {/* Condividi e preferiti */}
                  <div className="flex items-center space-x-3">
                    <button
                      onClick={shareProduct}
                      className="p-2 text-muted-foreground hover:text-primary transition-colors"
                      aria-label="Condividi prodotto"
                    >
                      <Share2 className="h-5 w-5" />
                    </button>
                  </div>
                </div>
              </div>

              {/* Prezzi */}
              <div className="mb-6">
                {variant && hasDiscount(variant) ? (
                  <div className="flex items-center space-x-4">
                    <span className="text-3xl font-bold text-primary">
                      {formatEuro(variant.price)}
                    </span>
                    <span className="text-xl text-muted-foreground line-through">
                      {formatEuro(variant.original_price!)}
                    </span>
                    {!!variant.discount_percent && (
                      <span className="bg-red-100 dark:bg-red-900/30 text-red-800 dark:text-red-200 px-2 py-1 rounded text-sm font-medium">
                        -{variant.discount_percent}%
                      </span>
                    )}
                  </div>
                ) : (
                  <span className="text-3xl font-bold text-primary">
                    {variant ? formatEuro(variant.price) : "Non disponibile"}
                  </span>
                )}
              </div>
            </div>

            {/* Formato */}
            {variants.length > 0 && (
              <div className="bg-card rounded-lg p-4 border">
                <span className="block text-sm font-medium text-foreground/80 mb-3">
                  Formato:
                </span>
                <VariantPicker
                  variants={variants}
                  selectedId={variantId}
                  onSelect={(v) => setVariantId(v.id)}
                />
              </div>
            )}

            {/* Disponibilità */}
            <div className="bg-card rounded-lg p-4 border">
              <div className="flex items-center justify-between mb-2">
                <span className="font-medium text-foreground">
                  Disponibilità:
                </span>
                {stock > 0 ? (
                  <span className="text-green-600 dark:text-green-400 font-medium">
                    <Check className="inline h-4 w-4 mr-1" />
                    Disponibile
                  </span>
                ) : (
                  <span className="text-red-600 dark:text-red-400 font-medium">
                    <X className="inline h-4 w-4 mr-1" />
                    Esaurito
                  </span>
                )}
              </div>

              {stock > 0 && stock <= 10 && (
                <div className="text-amber-600 dark:text-amber-400 text-sm">
                  Ultimi pezzi disponibili, affrettati!
                </div>
              )}
            </div>

            {/* Descrizione breve */}
            {product.description && (
              <div className="bg-card rounded-lg p-4 border">
                <p className="text-muted-foreground leading-relaxed">
                  {product.description}
                </p>
              </div>
            )}

            {/* Selezione quantità */}
            <div className="bg-card rounded-lg p-4 border">
              <label className="block text-sm font-medium text-foreground/80 mb-3">
                Quantità:
              </label>
              <div className="flex items-center space-x-4">
                <div className="flex items-center border border-border rounded-lg">
                  <button
                    onClick={() => handleQuantityChange(quantity - 1)}
                    disabled={quantity <= 1}
                    className="p-3 text-muted-foreground hover:bg-muted disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    <Minus className="h-4 w-4" />
                  </button>
                  <span className="px-4 py-2 text-lg font-medium text-foreground min-w-[3rem] text-center">
                    {quantity}
                  </span>
                  <button
                    onClick={() => handleQuantityChange(quantity + 1)}
                    disabled={quantity >= stock}
                    className="p-3 text-muted-foreground hover:bg-muted disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    <Plus className="h-4 w-4" />
                  </button>
                </div>

                <button
                  onClick={handleAddToCart}
                  disabled={stock === 0}
                  className={`flex-1 flex items-center justify-center space-x-2 py-3 px-6 rounded-lg font-medium transition-all ${
                    stock > 0
                      ? addedToCart
                        ? "bg-green-600 hover:bg-green-700 text-white"
                        : "bg-primary hover:bg-primary/90 text-primary-foreground"
                      : "bg-muted cursor-not-allowed text-muted-foreground"
                  }`}
                >
                  <ShoppingCart className="h-5 w-5" />
                  <span>
                    {stock > 0
                      ? addedToCart
                        ? "Aggiorna carrello"
                        : "Aggiungi al carrello"
                      : "Esaurito"}
                  </span>
                </button>
              </div>
            </div>

            {/* Info categoria e SKU */}
            <div className="bg-card rounded-lg p-4 border">
              <div className="grid grid-cols-2 gap-4 text-sm">
                <div>
                  <span className="text-muted-foreground">
                    Categoria:
                  </span>
                  <p className="text-foreground font-medium">
                    {product.category?.toUpperCase() || "Nessuna categoria"}
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Descrizione lunga e dettagli */}
        {product.long_description && (
          <div className="mb-16 bg-card rounded-lg p-6 shadow-sm">
            <h2 className="text-2xl font-bold text-foreground mb-6">
              Descrizione Completa
            </h2>

            <div className="prose prose-gray dark:prose-invert max-w-none">
              <div
                className="text-muted-foreground leading-relaxed"
                dangerouslySetInnerHTML={{ __html: sanitizeHtml(product.long_description || "") }}
              />
            </div>
          </div>
        )}

        {/* Schede prodotto */}
        <div className="mb-16">
          <div className="border-b border-border">
            <nav className="flex space-x-8">
              {["reviews"].map((tab) => (
                <button
                  key={tab}
                  onClick={() => setActiveTab(tab)}
                  className={`py-4 px-1 border-b-2 font-medium text-sm transition-colors ${
                    activeTab === tab
                      ? "border-primary text-primary"
                      : "border-transparent text-muted-foreground hover:text-foreground hover:border-primary/40"
                  }`}
                >
                  {tab === "reviews" && `Recensioni (${reviews.length})`}
                </button>
              ))}
            </nav>
          </div>

          <div className="py-8">
            {/* Scheda Recensioni */}
            {activeTab === "reviews" && (
              <div className="space-y-8">
                {/* Form recensione */}
                {user ? (
                  <div className="bg-card rounded-lg p-6 shadow-sm">
                    <h3 className="text-lg font-semibold text-foreground mb-6">
                      Scrivi una Recensione
                    </h3>

                    <div className="space-y-6">
                      <div>
                        <label className="block text-sm font-medium text-foreground/80 mb-3">
                          Valutazione
                        </label>
                        <div className="flex space-x-1">
                          {[1, 2, 3, 4, 5].map((star) => (
                            <button
                              key={star}
                              type="button"
                              onClick={() => setRating(star)}
                              className="focus:outline-none transition-transform hover:scale-110"
                            >
                              <Star
                                className={`h-8 w-8 ${
                                  star <= rating
                                    ? "text-yellow-400 fill-current"
                                    : "text-muted-foreground/40"
                                }`}
                              />
                            </button>
                          ))}
                        </div>
                      </div>

                      <div>
                        <label
                          htmlFor="review"
                          className="block text-sm font-medium text-foreground/80 mb-3"
                        >
                          La tua Recensione
                        </label>
                        <textarea
                          id="review"
                          rows={4}
                          value={reviewText}
                          onChange={(e) => setReviewText(e.target.value)}
                          className="w-full px-4 py-3 rounded-lg border border-border bg-card text-foreground focus:ring-2 focus:ring-primary focus:border-transparent"
                          placeholder="Condividi la tua esperienza con questo prodotto..."
                        />
                      </div>

                      <button
                        onClick={submitReview}
                        disabled={!reviewText.trim()}
                        className="bg-primary text-primary-foreground px-8 py-3 rounded-lg font-medium hover:bg-primary/90 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                      >
                        Invia Recensione
                      </button>
                    </div>
                  </div>
                ) : (
                  <div className="bg-blue-50 dark:bg-blue-900/20 p-6 rounded-lg">
                    <p className="text-blue-700 dark:text-blue-300 text-center">
                      Per favore{" "}
                      <Link
                        to="/auth"
                        className="text-primary hover:underline font-medium"
                      >
                        accedi
                      </Link>{" "}
                      per lasciare una recensione.
                    </p>
                  </div>
                )}

                {/* Lista recensioni */}
                {reviews.length > 0 ? (
                  <div className="space-y-6">
                    <h3 className="text-xl font-semibold text-foreground">
                      Recensioni dei Clienti ({reviews.length})
                    </h3>

                    {reviews.map((review) => (
                      <div
                        key={review.id}
                        className="bg-card rounded-lg p-6 shadow-sm"
                      >
                        <div className="flex items-start justify-between mb-4">
                          <div className="flex items-center space-x-3">
                            <div className="flex">
                              {[1, 2, 3, 4, 5].map((star) => (
                                <Star
                                  key={star}
                                  className={`h-5 w-5 ${
                                    star <= review.rating
                                      ? "text-yellow-400 fill-current"
                                      : "text-muted-foreground/40"
                                  }`}
                                />
                              ))}
                            </div>
                          </div>
                          <span className="text-sm text-muted-foreground">
                            {new Date(review.created_at).toLocaleDateString(
                              "it-IT"
                            )}
                          </span>
                        </div>

                        <p className="text-foreground/80 mb-4 leading-relaxed">
                          {review.comment}
                        </p>

                        <div className="flex items-center text-sm text-muted-foreground">
                          {/* Mostra username se disponibile */}
                          {review.users && (
                            <span>Di {review.users.username}</span>
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                ) : (
                  <div className="text-center py-12">
                    <Star className="h-12 w-12 text-muted-foreground/40 mx-auto mb-4" />
                    <p className="text-muted-foreground text-lg">
                      Ancora nessuna recensione. Sii il primo a recensire questo
                      prodotto!
                    </p>
                  </div>
                )}
              </div>
            )}
          </div>
        </div>

        {/* Prodotti correlati */}
        {relatedProducts.length > 0 && (
          <div className="mb-16">
            <h2 className="text-2xl font-bold text-foreground mb-8">
              Prodotti Correlati
            </h2>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
              {relatedProducts.map((relatedProduct) => (
                <ProductCard
                  key={relatedProduct.id}
                  product={relatedProduct}
                  onQuickView={(product) => navigate(`/products/${product.id}`)}
                />
              ))}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
